import { createFiniteDecimalFromAnyString } from "@goodfill/decimal";
import { TradeTicketEntity, OrderTicketEntity } from "@goodfill/domain-entities";
import { calculateProfitAndLoss, includeGoodfillCommissionWithBasePrice } from "@goodfill/domain-math";
import {
  Amount,
  asAmount,
  asBondPriceNominal,
  asProfitAndLoss,
  Currency,
  Money,
  OrderDirection,
  OrderStatus,
  ProfitAndLoss,
} from "@goodfill/domain-properties";
import { tradeTicketApiToModel, orderTicketApiToModel, TradeTicket, OrderTicket } from "@goodfill/models-typescript";
import Decimal from "decimal.js";
import { PortfolioPositionTradeTicketListItem } from "./doPortfolioCustomListPositions";

const ZERO_AMOUNT = asAmount(new Decimal(0));

export interface PortfolioPositionTradeTicket extends TradeTicketEntity {
  orderTicket: OrderTicketEntity;
  alreadySold: Amount;

  amount: Amount; // Face Amount
  lockedInOrder: Amount;
  availableToSell: Amount;

  pnlRealized: ProfitAndLoss;
  plnUnrealizedInOrders: ProfitAndLoss;
}

interface CalculationResult {
  alreadySold: Amount;
  lockedInOrder: Amount;
  pnlRealized: Amount;
  plnUnrealizedInOrders: Amount;
}

type CalculationMap = {
  [Property in OrderStatus]: (
    acc: CalculationResult,
    closingOrder: OrderTicket,
    position: TradeTicketEntity,
  ) => CalculationResult;
};

function sumFillAmount(closingOrderTicket: OrderTicket): Amount {
  return asAmount(
    (closingOrderTicket.tradeTickets?.items ?? ([] as TradeTicket[])).reduce(
      (sum, closingTradeTicket) => sum.plus(closingTradeTicket?.amount ?? new Decimal(0)),
      new Decimal(0),
    ),
  );
}

function calculateCanceled(
  acc: CalculationResult,
  closingOrder: OrderTicket,
  position: TradeTicketEntity,
): CalculationResult {
  return {
    alreadySold: asAmount(acc.alreadySold.plus(sumFillAmount(closingOrder))),
    lockedInOrder: acc.lockedInOrder,
    pnlRealized: acc.pnlRealized,
    plnUnrealizedInOrders: acc.plnUnrealizedInOrders,
  };
}

function calculateNonCanceled(
  acc: CalculationResult,
  closingOrder: OrderTicket,
  position: TradeTicketEntity,
): CalculationResult {
  const closingOrderFaceAmount = asAmount(createFiniteDecimalFromAnyString(closingOrder.faceAmount, "faceAmount"));
  const closingOrderFaceMoneyAmount: Money = { amount: closingOrderFaceAmount, currency: Currency.USD };
  const closingOrderBondPrice = asBondPriceNominal(
    createFiniteDecimalFromAnyString(closingOrder.bondPrice, "bondPrice"),
  );
  const soldAmount = sumFillAmount(closingOrder);

  const positionMoneyAmount: Money = { amount: position.amount, currency: Currency.USD };

  const loadedBuyPrice = includeGoodfillCommissionWithBasePrice(
    position.bondPrice,
    positionMoneyAmount,
    OrderDirection.Buy,
  );

  const pnlRealized = closingOrder.tradeTickets?.items.reduce((acc, closingTradeTicker) => {
    if (closingTradeTicker === null) {
      return acc;
    }

    const amount = asAmount(createFiniteDecimalFromAnyString(closingTradeTicker.amount, "amount"));
    const sellPrice = asBondPriceNominal(createFiniteDecimalFromAnyString(closingTradeTicker.bondPrice, "bondPrice"));
    const moneyAmount: Money = { amount, currency: Currency.USD };
    const loadedSellPrice = includeGoodfillCommissionWithBasePrice(sellPrice, moneyAmount, OrderDirection.Sell);

    return calculateProfitAndLoss(moneyAmount, loadedBuyPrice, loadedSellPrice).amount;
  }, ZERO_AMOUNT);

  const lockedInOrderAmount = asAmount(closingOrderFaceAmount.minus(soldAmount));
  const lockedInOrderMoneyAmount: Money = { amount: lockedInOrderAmount, currency: Currency.USD };

  const loadedClosingOrderSellPrice = includeGoodfillCommissionWithBasePrice(
    closingOrderBondPrice,
    closingOrderFaceMoneyAmount,
    OrderDirection.Sell,
  );

  const plnUnrealizedInOrders = calculateProfitAndLoss(
    lockedInOrderMoneyAmount,
    loadedBuyPrice,
    loadedClosingOrderSellPrice,
  );

  return {
    alreadySold: asAmount(acc.alreadySold.plus(soldAmount)),
    lockedInOrder: asAmount(acc.lockedInOrder.plus(lockedInOrderAmount)),
    pnlRealized: asAmount(acc.pnlRealized.plus(pnlRealized ?? ZERO_AMOUNT)),
    plnUnrealizedInOrders: asAmount(acc.plnUnrealizedInOrders.plus(plnUnrealizedInOrders.amount ?? ZERO_AMOUNT)),
  };
}

const calculationMap: CalculationMap = {
  // Non-canceled
  [OrderStatus.Open]: calculateNonCanceled,
  [OrderStatus.WaitingForTrader]: calculateNonCanceled,
  [OrderStatus.Closed]: calculateNonCanceled,
  [OrderStatus.PendingCancelApproval]: calculateNonCanceled,
  [OrderStatus.PendingModifyApproval]: calculateNonCanceled,
  [OrderStatus.FillSubmittedToBroker]: calculateNonCanceled,
  [OrderStatus.FillAcknowledgedByBroker]: calculateNonCanceled,
  [OrderStatus.CancelSubmittedToBroker]: calculateNonCanceled,
  [OrderStatus.ModifySubmittedToBroker]: calculateNonCanceled,
  [OrderStatus.FullyFilled]: calculateNonCanceled,

  // Canceled
  [OrderStatus.Canceled]: calculateCanceled,
  [OrderStatus.CanceledByCustomer]: calculateCanceled,
  [OrderStatus.CanceledByTrader]: calculateCanceled,
  [OrderStatus.CanceledAndReplaced]: calculateCanceled,
  [OrderStatus.Expired]: calculateCanceled,
};

export function convertApiToPortfolioPositionTradeTicket(
  api: PortfolioPositionTradeTicketListItem,
): PortfolioPositionTradeTicket {
  const tradeTicketEntity = tradeTicketApiToModel(api);

  const closingOrders = api.closingOrderTickets?.items ?? ([] as OrderTicket[]);

  const { alreadySold, lockedInOrder, pnlRealized, plnUnrealizedInOrders } = closingOrders.reduce(
    (acc, closingOrder): CalculationResult => {
      if (closingOrder === null) {
        return acc;
      }

      return calculationMap[closingOrder.status](acc, closingOrder, tradeTicketEntity);
    },
    {
      alreadySold: ZERO_AMOUNT,
      lockedInOrder: ZERO_AMOUNT,
      pnlRealized: ZERO_AMOUNT,
      plnUnrealizedInOrders: ZERO_AMOUNT,
    },
  );

  const availableToSell = asAmount(tradeTicketEntity.amount.minus(alreadySold).minus(lockedInOrder));

  return {
    ...tradeTicketEntity,
    orderTicket: orderTicketApiToModel(api.orderTicket),
    alreadySold,

    amount: tradeTicketEntity.amount, // original Face Amount
    lockedInOrder: lockedInOrder,
    availableToSell,

    pnlRealized: asProfitAndLoss({ amount: pnlRealized, currency: Currency.USD }),
    plnUnrealizedInOrders: asProfitAndLoss({ amount: plnUnrealizedInOrders, currency: Currency.USD }),
  };
}
